import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, OnDestroy, Output } from '@angular/core';
import { ColDef, GridOptions, ICellRendererParams, Utils } from 'ag-grid-community';
import { RowNode } from 'ag-grid-community/main';
import { debounce, defaults, isNil } from 'lodash';
import { BehaviorSubject } from 'rxjs';
import { comparators } from '../core/comparators';
import { SortModel } from '../covance-ag-grid/ag-export.component';
import { CovanceGridColumn } from './covance-grid-column';
import { CellTooltipAgRendererComponent } from './renderers/cell-tooltip-ag-renderer.component';
import { HeaderGroupRenderer } from './renderers/header-group-renderer.component';
import { HeaderTooltipAgRendererComponent } from './renderers/header-tooltip-ag-renderer.component';

@Component({
    selector: 'covance-ag-grid',
    template: require('./covance-ag-grid.component.html'),
    styles: [require('./covance-ag-grid.component.scss')],
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class CovanceAgGridComponent implements OnChanges, OnDestroy {

    @Output()
    public rowClicked = new EventEmitter();

    @Output()
    public saveClicked = new EventEmitter();

    @Output()
    public printClicked = new EventEmitter();

    @Output()
    public checkBoxClicked = new EventEmitter();

    @Output()
    public displayedColumnsChanged = new EventEmitter();

    @Output()
    public sortChanged = new EventEmitter();

    @Output()
    public quickFilterChanged = new EventEmitter<string>();

    @Input()
    public set dataSource(value: any[]) {
        this.data = value;
        this.handleOverlays();
    }

    public get dataSource() {
        return this.data;
    }

    @Input()
    public set columnDefs(value: CovanceGridColumn[]) {
        this.columns = value;
        this.handleOverlays();
        // the code below is for fixing AgGrid's issues with changing columns on the fly. 
        // In particular: column order unpredictably changes and grouping doesn't work. 
        // We hope they will fix the issues soon.
        const gridApi = this.options && this.options.api;
        if (gridApi) {
            gridApi.setColumnDefs(null);
            setTimeout(() => {
                gridApi.setColumnDefs(value);
                gridApi.redrawRows();
            });
        }
    }

    public get columnDefs() {
        return this.columns;
    }

    @Input()
    public exportGrid: GridOptions = null;

    @Input()
    public headerHeight: number;

    @Input()
    public groupUseEntireRow = false;

    @Input()
    public set gridOptions(value: GridOptions) {
        this.options = value;
        defaults(value, this.defaultGridOptions);
    }

    public get gridOptions() {
        return this.options;
    }

    @Input()
    public showToolBar = true;

    @Input()
    public showQuickFilter = true;

    @Input()
    public showExport = true;

    @Input()
    public exportDisclaimer: string = null;

    @Input()
    public exportData: { [key: string]: string }[];

    @Input()
    public showGrid = true;

    @Input()
    public showSave = false;

    @Input()
    public showPrint = false;

    @Input()
    public showTotal = false;

    @Input()
    public showCount = false;

    @Input()
    public quickFilter: string;

    @Input()
    public checkBoxLabel: string = null;

    public selectedRowNodes = new BehaviorSubject<RowNode[]>([]);

    public get enableToolBar() {
        return (this.showQuickFilter || this.showExport) && this.showToolBar;
    }

    private readonly defaultGridOptions: GridOptions = {
        defaultColDef: {
            comparator: function (a, b) {
                switch (typeof a) {
                    case 'string':
                        return comparators.stringComparator(a, b);
                    default:
                        return Utils.defaultComparator(a, b);
                }
            }
        },
        enableFilter: true,
        enableColResize: true,
        enableSorting: true,
        suppressDragLeaveHidesColumns: true,
        animateRows: true,
        rowHeight: 25,
        minColWidth: 75,
        headerHeight: 25,
        suppressCellSelection: true,
        groupDefaultExpanded: 1,
        groupUseEntireRow: true,
        groupRowInnerRendererFramework: HeaderGroupRenderer,
        overlayNoRowsTemplate: `<span>No Data</span>`,
        //magic template for mdl-spinner. ag grid requeires it to be plain html. no angular no js so we cant use mdl-spinner tag here.
        overlayLoadingTemplate: `<div>
            <div class="mdl-spinner is-active is-upgraded">
                <div class="mdl-spinner__layer mdl-spinner__layer-1"><div class="mdl-spinner__circle-clipper mdl-spinner__left">
                    <div class="mdl-spinner__circle"></div></div><div class="mdl-spinner__gap-patch"><div class="mdl-spinner__circle"></div></div><div class="mdl-spinner__circle-clipper mdl-spinner__right"><div class="mdl-spinner__circle"></div></div>
                </div>
                <div class="mdl-spinner__layer mdl-spinner__layer-2"><div class="mdl-spinner__circle-clipper mdl-spinner__left">
                    <div class="mdl-spinner__circle"></div></div><div class="mdl-spinner__gap-patch"><div class="mdl-spinner__circle"></div></div><div class="mdl-spinner__circle-clipper mdl-spinner__right"><div class="mdl-spinner__circle"></div></div>
                </div>
                <div class="mdl-spinner__layer mdl-spinner__layer-3"><div class="mdl-spinner__circle-clipper mdl-spinner__left">
                    <div class="mdl-spinner__circle"></div></div><div class="mdl-spinner__gap-patch"><div class="mdl-spinner__circle"></div></div><div class="mdl-spinner__circle-clipper mdl-spinner__right"><div class="mdl-spinner__circle"></div></div>
                </div>
                <div class="mdl-spinner__layer mdl-spinner__layer-4"><div class="mdl-spinner__circle-clipper mdl-spinner__left">
                    <div class="mdl-spinner__circle"></div></div><div class="mdl-spinner__gap-patch"><div class="mdl-spinner__circle"></div></div><div class="mdl-spinner__circle-clipper mdl-spinner__right"><div class="mdl-spinner__circle"></div></div>
                </div>
            </div>
        </div>`,
        suppressMovableColumns: true,
        suppressContextMenu: true,
        suppressPropertyNamesCheck: true,
        suppressRowTransform: true,
        onGridSizeChanged: function (params) {
            params.api.sizeColumnsToFit();
        }
    };

    private options: GridOptions;

    private data: any[];
    private columns: ColDef[] = [];

    private handleOverlays = debounce(() => {
        if (this.gridOptions && this.gridOptions.api) {
            this.gridOptions.api.hideOverlay();
            if (!this.data) {
                this.gridOptions.api.showLoadingOverlay();
            } else if (this.data.length === 0) {
                this.gridOptions.api.showNoRowsOverlay();
            }
        }
    });

    @Input()
    public exportHandleSort = (sortModels: SortModel[]): SortModel[] => {
        return sortModels;
    }

    public ngOnChanges() {
        if (!this.gridOptions) {
            this.gridOptions = this.defaultGridOptions;
        }

        this.setTooltips();
        this.setRowHeight();

        if (this.headerHeight) {
            this.gridOptions.headerHeight = this.headerHeight;
        }

        if (this.groupUseEntireRow) {
            this.gridOptions.groupUseEntireRow = true;
        }
    }

    public ngOnDestroy() {
        if (this.gridOptions.api) {
            this.gridOptions.api.removeEventListener('filterChanged', this.pushSelectedNodes);
            this.gridOptions.api.removeEventListener('sortChanged', this.pushSelectedNodes);
        }
    }

    public onSortChanged(event: Event) {
        this.sortChanged.emit(event);
    }

    public onDisplayedColumnsChanged(event: Event) {
        this.displayedColumnsChanged.emit(event);
    }

    public onRowClicked(event: Event) {
        this.rowClicked.emit(event);
    }

    public onRowSelected() {
        this.pushSelectedNodes();
    }

    public onPrintClicked(event: Event) {
        this.printClicked.emit(event);
    }

    public onSaveClicked(event: Event) {
        this.saveClicked.emit(event);
    }

    public onCheckBoxClicked(event: Event) {
        this.checkBoxClicked.emit(event);
    }

    public onQuickFilterChange(value: string) {
        this.quickFilter = value;

        if (this.gridOptions && this.gridOptions.api) {
            this.gridOptions.api.setQuickFilter(value);
        }

        if (this.exportGrid && this.exportGrid.api) {
            this.exportGrid.api.setQuickFilter(value);
        }

        this.quickFilterChanged.emit(value);
    }

    public resetQuickFilter() {
        this.onQuickFilterChange('');
    }

    public pushSelectedNodes = () => {
        if (!this.gridOptions.api) {
            return;
        }

        this.gridOptions.api.sizeColumnsToFit();
        this.gridOptions.api.setQuickFilter(this.quickFilter);

        const selectedNodes: RowNode[] = [];

        this.gridOptions.api.forEachNodeAfterFilterAndSort(rowNode => {
            if (rowNode.isSelected()) {
                selectedNodes.push(rowNode);
            }
        });

        debounce(() => this.selectedRowNodes.next(selectedNodes))();
    }

    private setTooltips() {
        if (!this.columnDefs) {
            return;
        }

        this.columnDefs.forEach(colDef => {
            if (!colDef.cellRendererFramework && !colDef.cellRenderer && !colDef.isCellTooltipOff) {
                colDef.cellRendererFramework = CellTooltipAgRendererComponent;
            }

            if (!colDef.headerComponentFramework && !colDef.headerComponent && !colDef.isHeaderTooltipOff) {
                colDef.headerComponentFramework = HeaderTooltipAgRendererComponent;
            }
        });
    }

    private setRowHeight() {
        if (!this.columnDefs) {
            return;
        }

        const multilineColDefs = this.columnDefs.filter(colDef => !!colDef.getLineCount || !!colDef.getRowHeight);

        if (multilineColDefs.length === 0) {
            return;
        }

        const container = {
            borderTop: 1,
            borderBottom: 1,
            PaddingTop: 5
        };

        const offset = container.borderTop + container.borderBottom + container.PaddingTop;

        this.gridOptions.getRowHeight = (params: ICellRendererParams) => {
            let maxRowsCount = 1;

            if (isNil(params.node.rowGroupIndex)) {
                multilineColDefs.filter(colDef => !!colDef.getLineCount).forEach(colDef => {
                    const count = colDef.getLineCount(params.data);
                    if (count > maxRowsCount) {
                        maxRowsCount = count;
                    }
                });
            }

            let maxSize = maxRowsCount * (this.gridOptions.rowHeight - offset) + offset;

            if (isNil(params.node.rowGroupIndex)) {
                this.columnDefs.filter(colDef => !!colDef.getRowHeight).forEach(colDef => {
                    const size = colDef.getRowHeight(params.data);
                    if (size > maxSize) {
                        maxSize = size;
                    }
                });
            }

            return maxSize;
        };
    }
}